import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AuthService } from '../../services/auth/auth.service';
import { Profile } from '../../services/auth/auth.model';
import { Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { mergeDeep } from 'functions/src/shared/tools';
import * as firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/storage';
import 'firebase/functions';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss']
})
export class UserComponent implements OnInit {

  public user = new Profile();
  public user$: Observable<Profile>;
  public new = true;
  private _userID = '0';
  public signaturePreview: SafeHtml = null;
  public showSignaturePreview = false;

  constructor(
    protected _cd: ChangeDetectorRef,
    public auth: AuthService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected sanitizer: DomSanitizer,
  ) { 
  }

  ngOnInit(): void {
    this._userID = this.route.snapshot.paramMap.get('id');
    this._initProfile();
  }

  private _initProfile() {
    if (this._userID && this._userID != '0') {
      this.new = false;
      this.user$ = this.auth.initUser(this._userID);
      this.user$.subscribe((user) => {
        this.user.fromFB(user);
        this.user.values = mergeDeep({}, this.user.values);
        this._cd.markForCheck();
      });
    } else {
      this._userID = '0';
    }
  }

  saveNewUser() {
    this.auth.createInvitation(this.user).then(() => {
      this.router.navigate([`/settings/users`]);
    });
  }

  saveUser() {
    this.auth.saveProfile(this.user);
    this.router.navigate([`/settings/users`]);
  }

  blockUser(){
    this.auth.blockProfile(this.user);
  }

  activeUser(){
    this.auth.activeProfile(this.user);
  }

  syncGmailSignature() {
    // Get the user's main agency
    const mainAgency = this.user.values?.agency;
    
    if (!mainAgency) {
      console.warn('User does not have a main agency assigned');
      return;
    }

    // Create a sync request in a separate subcollection to avoid infinite loops
    firebase.firestore().collection('users').doc(this._userID).collection('gmailSyncRequest').doc('sync').set({
      status: 'pending',
      requestedAt: firebase.firestore.FieldValue.serverTimestamp(),
    }).then(() => {
      console.log(`Gmail signature sync requested for user ${this._userID} with agency ${mainAgency}`);
      this._cd.markForCheck();
    }).catch((error) => {
      console.error('Error requesting Gmail signature sync:', error);
    });
  }

  async generateSignaturePreview() {
    try {
      const template = this.user.values?.signatureTemplate || 'nestenn';
      const agencyId = this.user.values?.agency;

      if (!agencyId) {
        console.warn('User does not have a main agency assigned');
        return;
      }

      // Fetch agency data
      const agencyQuery = await firebase.firestore()
        .collection('agencies')
        .where('id', '==', agencyId)
        .limit(1)
        .get();

      if (agencyQuery.empty) {
        console.warn(`Agency with id ${agencyId} not found`);
        return;
      }

      const agencyData = agencyQuery.docs[0].data();

      const templateHtml = await this.loadTemplateHtml(template);

      const htmlSignature = this.substituteTemplateVars(templateHtml, {
        displayName: this.user.values?.fullname || `${this.user.values?.firstname || ''} ${this.user.values?.lastname || ''}`.trim(),
        phone: this.formatPhoneNumber(this.user.values?.tel),
        photoURL: this.user.values?.extPhoto,
        jobTitle: this.user.values?.jobTitle,
        agencyName: agencyData.name,
        agencyAddress: agencyData.address || agencyData.addressDisplay,
        agencyPhone: this.formatPhoneNumber(agencyData.tel),
        linkedinURL: this.user.values?.linkedinURL || '',
        facebookURL: this.user.values?.facebookURL || '',
        instagramURL:this.user.values?.instagramURL || '',
        youtubeURL: this.user.values?.youtubeURL || '',
      });

      this.signaturePreview = this.sanitizer.bypassSecurityTrustHtml(htmlSignature);
      this.showSignaturePreview = true;
      this._cd.markForCheck();
    } catch (error) {
      console.error('Error generating signature preview:', error);
    }
  }

  private async loadTemplateHtml(templateName: string): Promise<string> {
    // Call the callable function to avoid Storage CORS issues
    const fn = firebase.app().functions('us-central1').httpsCallable('getGmailSignatureTemplate');
    const resp = await fn({ template: templateName });
    return resp.data?.html || '';
  }

  private substituteTemplateVars(html: string, vars: Record<string, string>): string {
    return html
      .replace(/{{displayName}}/g, vars.displayName || '')
      .replace(/{{phone}}/g, vars.phone || '')
      .replace(/{{photoURL}}/g, vars.photoURL || '')
      .replace(/{{jobTitle}}/g, vars.jobTitle || '')
      .replace(/{{agencyName}}/g, vars.agencyName || '')
      .replace(/{{agencyAddress}}/g, vars.agencyAddress || '')
      .replace(/{{agencyPhone}}/g, vars.agencyPhone || '')
      .replace(/{{linkedinURL}}/g, vars.linkedinURL || '')
      .replace(/{{facebookURL}}/g, vars.facebookURL || '')
      .replace(/{{instagramURL}}/g, vars.instagramURL || '')
      .replace(/{{youtubeURL}}/g, vars.youtubeURL || '');
  }

  private formatPhoneNumber(phone: string): string {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '').replace(/^230/, '');
    if (cleaned.length < 8) return phone;
    return `(+230) ${cleaned.slice(0, 4)} ${cleaned.slice(4)}`;
  }

}
